import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE, formatApiError, getToken } from "@/lib/api";
import {
  Plus, Search, KeyRound, Ban, CheckCircle2, Edit, Trash2, Loader2,
  Users, Route as RouteIcon, Building2, ShieldCheck, Upload, FileSpreadsheet, FileText, Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 15;

function downloadFromApi(path, params, filename) {
  const token = getToken();
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params || {}).forEach(([k, v]) => v !== undefined && v !== "" && url.searchParams.set(k, v));
  return fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } }).then(async (r) => {
    if (!r.ok) throw new Error(await r.text());
    const blob = await r.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = objUrl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(objUrl);
  });
}

export default function Cadastros() {
  const [tab, setTab] = useState("motoristas");
  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">Configurações</p>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Cadastros</h1>
        <p className="mt-1 text-sm text-slate-500">
          Central de dados mestres da operação · uso restrito a administradores
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-5">
          <TabsTrigger value="motoristas" data-testid="cad-tab-motoristas">
            <Users className="mr-1.5 h-4 w-4" /> Motoristas
          </TabsTrigger>
          <TabsTrigger value="supervisores" data-testid="cad-tab-supervisores">
            <ShieldCheck className="mr-1.5 h-4 w-4" /> Supervisores
          </TabsTrigger>
          <TabsTrigger value="rotas" data-testid="cad-tab-rotas">
            <RouteIcon className="mr-1.5 h-4 w-4" /> Rotas
          </TabsTrigger>
          <TabsTrigger value="cos" data-testid="cad-tab-cos">
            <Building2 className="mr-1.5 h-4 w-4" /> Centros
          </TabsTrigger>
          <TabsTrigger value="usuarios" data-testid="cad-tab-usuarios">
            <ShieldCheck className="mr-1.5 h-4 w-4" /> Usuários
          </TabsTrigger>
        </TabsList>

        <TabsContent value="motoristas"><MotoristasTab /></TabsContent>
        <TabsContent value="supervisores"><SupervisoresTab /></TabsContent>
        <TabsContent value="rotas"><RotasTab /></TabsContent>
        <TabsContent value="cos"><COsTab /></TabsContent>
        <TabsContent value="usuarios"><UsuariosTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- MOTORISTAS ---------------- */
const DRIVER_STATUSES = [
  { v: "ativo", label: "Ativo", color: "bg-green-100 text-green-800 border-green-200" },
  { v: "ferias", label: "Férias", color: "bg-blue-100 text-blue-800 border-blue-200" },
  { v: "afastado", label: "Afastado", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { v: "desligado", label: "Desligado", color: "bg-red-100 text-red-800 border-red-200" },
  { v: "inativo", label: "Inativo", color: "bg-slate-200 text-slate-700 border-slate-300" },
];
const emptyDriver = {
  nome: "", cpf: "", matricula: "", telefone: "", email: "",
  veiculo: "", placa: "", modelo_veiculo: "", tipo_veiculo: "",
  rota: "", supervisor: "", centro_operacional: "", data_admissao: "",
  senha_temporaria: "trocar123",
};

function MotoristasTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fSupervisor, setFSupervisor] = useState("all");
  const [fCO, setFCO] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(emptyDriver);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/drivers"); setRows(data || []); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const supervisorOpts = useMemo(() => Array.from(new Set(rows.map((r) => r.supervisor).filter(Boolean))).sort(), [rows]);
  const coOpts = useMemo(() => Array.from(new Set(rows.map((r) => r.centro_operacional).filter(Boolean))).sort(), [rows]);

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return rows.filter((r) => {
      if (t && !(r.nome.toLowerCase().includes(t) || r.cpf.includes(t) || (r.matricula || "").toLowerCase().includes(t))) return false;
      if (fSupervisor !== "all" && r.supervisor !== fSupervisor) return false;
      if (fCO !== "all" && r.centro_operacional !== fCO) return false;
      if (fStatus !== "all" && r.status !== fStatus) return false;
      return true;
    });
  }, [rows, q, fSupervisor, fCO, fStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  function openCreate() { setEditing(null); setForm(emptyDriver); setOpen(true); }
  function openEdit(d) { setEditing(d); setForm({ ...emptyDriver, ...d }); setOpen(true); }

  async function save() {
    setSaving(true);
    try {
      if (editing) { await api.put(`/drivers/${editing.id}`, form); toast.success("Motorista atualizado"); }
      else { await api.post("/drivers", form); toast.success("Motorista cadastrado"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }
  async function block(d) {
    try {
      if (d.status === "ativo") { await api.post(`/drivers/${d.id}/block`); toast.success(`${d.nome} bloqueado`); }
      else { await api.post(`/drivers/${d.id}/unblock`); toast.success(`${d.nome} reativado`); }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }
  async function resetPwd(d) {
    try {
      const r = await api.post(`/drivers/${d.id}/reset-password`);
      toast.success(`Nova senha: ${r.data.temp_password}`);
    } catch (err) { toast.error(formatApiError(err)); }
  }
  async function del(d) {
    if (!window.confirm(`Desligar ${d.nome}? O histórico será mantido, mas o motorista não poderá mais acessar.`)) return;
    try { await api.delete(`/drivers/${d.id}`); toast.success("Motorista desligado"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  }
  async function onImport(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/drivers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importado: ${r.data.created} criados, ${r.data.updated} atualizados${r.data.errors.length ? `, ${r.data.errors.length} erros` : ""}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  }
  async function exportFile(format) {
    try {
      await downloadFromApi("/drivers/export", { format }, `motoristas.${format === "pdf" ? "pdf" : "xlsx"}`);
      toast.success("Cadastro exportado");
    } catch { toast.error("Falha ao exportar"); }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-slate-500">{filtered.length} de {rows.length} cadastrados</p>
        <div className="ml-auto flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onImport} data-testid="drivers-import-file" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="drivers-import-btn">
            <Upload className="mr-1.5 h-4 w-4" /> Importar (.xlsx)
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportFile("xlsx")} className="border-green-700 text-green-700 hover:bg-green-50" data-testid="drivers-export-xlsx">
            <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportFile("pdf")} className="border-blue-700 text-blue-700 hover:bg-blue-50" data-testid="drivers-export-pdf">
            <FileText className="mr-1.5 h-4 w-4" /> PDF
          </Button>
          <Button size="sm" onClick={openCreate} className="bg-blue-900 hover:bg-blue-800" data-testid="drivers-add-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Novo Motorista
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Buscar por nome, CPF ou matrícula" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="drivers-search-input" />
        </div>
        <Select value={fSupervisor} onValueChange={setFSupervisor}>
          <SelectTrigger data-testid="drivers-filter-supervisor"><SelectValue placeholder="Supervisor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos supervisores</SelectItem>
            {supervisorOpts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fCO} onValueChange={setFCO}>
          <SelectTrigger data-testid="drivers-filter-co"><SelectValue placeholder="CO" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos COs</SelectItem>
            {coOpts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="drivers-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            {DRIVER_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Matrícula</TableHead>
                <TableHead>Veículo</TableHead>
                <TableHead>Rota</TableHead>
                <TableHead>Supervisor</TableHead>
                <TableHead>CO</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="py-10 text-center text-slate-400">Carregando…</TableCell></TableRow>}
              {!loading && pageRows.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-slate-400">Nenhum motorista.</TableCell></TableRow>}
              {pageRows.map((d) => {
                const s = DRIVER_STATUSES.find((x) => x.v === d.status) || DRIVER_STATUSES[0];
                return (
                  <TableRow key={d.id} data-testid={`driver-row-${d.id}`}>
                    <TableCell className="font-medium text-slate-900">{d.nome}<div className="text-xs text-slate-500">{d.cpf}</div></TableCell>
                    <TableCell>{d.matricula}</TableCell>
                    <TableCell className="text-sm text-slate-600">{d.placa || "—"}{d.modelo_veiculo ? <div className="text-xs text-slate-400">{d.modelo_veiculo}</div> : null}</TableCell>
                    <TableCell>{d.rota || "—"}</TableCell>
                    <TableCell>{d.supervisor || "—"}</TableCell>
                    <TableCell>{d.centro_operacional || "—"}</TableCell>
                    <TableCell><Badge className={`border ${s.color}`}>{s.label}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <IconBtn onClick={() => setViewing(d)} title="Visualizar" data-testid={`driver-view-${d.id}`}><Eye className="h-4 w-4" /></IconBtn>
                        <IconBtn onClick={() => openEdit(d)} title="Editar" data-testid={`driver-edit-${d.id}`}><Edit className="h-4 w-4" /></IconBtn>
                        <IconBtn onClick={() => resetPwd(d)} title="Resetar senha" data-testid={`driver-reset-${d.id}`}><KeyRound className="h-4 w-4" /></IconBtn>
                        <IconBtn onClick={() => block(d)} title={d.status === "ativo" ? "Bloquear" : "Reativar"} data-testid={`driver-block-${d.id}`}>
                          {d.status === "ativo" ? <Ban className="h-4 w-4 text-red-600" /> : <CheckCircle2 className="h-4 w-4 text-green-600" />}
                        </IconBtn>
                        <IconBtn onClick={() => del(d)} title="Excluir" data-testid={`driver-delete-${d.id}`}><Trash2 className="h-4 w-4 text-red-600" /></IconBtn>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-xs text-slate-500">
          <span>Página {page} de {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="drivers-page-prev">Anterior</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="drivers-page-next">Próxima</Button>
          </div>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar motorista" : "Novo motorista"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nome Completo" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} testId="form-nome" full />
            <Field label="CPF" value={form.cpf} onChange={(v) => setForm({ ...form, cpf: v })} testId="form-cpf" />
            <Field label="Matrícula" value={form.matricula} onChange={(v) => setForm({ ...form, matricula: v })} testId="form-matricula" />
            <Field label="Telefone" value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v })} testId="form-telefone" />
            <Field label="Email (opcional)" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="form-email" />
            <Field label="Placa" value={form.placa} onChange={(v) => setForm({ ...form, placa: v })} testId="form-placa" />
            <Field label="Modelo do veículo" value={form.modelo_veiculo} onChange={(v) => setForm({ ...form, modelo_veiculo: v })} testId="form-modelo" />
            <Field label="Tipo de veículo" value={form.tipo_veiculo} onChange={(v) => setForm({ ...form, tipo_veiculo: v })} testId="form-tipo" />
            <Field label="Rota principal" value={form.rota} onChange={(v) => setForm({ ...form, rota: v })} testId="form-rota" />
            <Field label="Supervisor" value={form.supervisor} onChange={(v) => setForm({ ...form, supervisor: v })} testId="form-supervisor" />
            <Field label="Centro Operacional (CO)" value={form.centro_operacional} onChange={(v) => setForm({ ...form, centro_operacional: v })} testId="form-co" />
            <Field label="Data admissão (opcional)" type="date" value={form.data_admissao || ""} onChange={(v) => setForm({ ...form, data_admissao: v })} testId="form-admissao" />
            {editing && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Status</Label>
                <Select value={form.status || "ativo"} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DRIVER_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!editing && (
              <Field label="Senha temporária" value={form.senha_temporaria} onChange={(v) => setForm({ ...form, senha_temporaria: v })} testId="form-senha" />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="form-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-w-2xl" data-testid="driver-view-dialog">
          <DialogHeader><DialogTitle>{viewing?.nome}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <KV label="CPF" v={viewing.cpf} />
              <KV label="Matrícula" v={viewing.matricula} />
              <KV label="Telefone" v={viewing.telefone} />
              <KV label="Email" v={viewing.email} />
              <KV label="Placa" v={viewing.placa} />
              <KV label="Modelo" v={viewing.modelo_veiculo} />
              <KV label="Tipo" v={viewing.tipo_veiculo} />
              <KV label="Rota" v={viewing.rota} />
              <KV label="Supervisor" v={viewing.supervisor} />
              <KV label="CO" v={viewing.centro_operacional} />
              <KV label="Admissão" v={viewing.data_admissao} />
              <KV label="Status" v={viewing.status} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- SUPERVISORES ---------------- */
function SupervisoresTab() {
  return <SimpleCadastro
    endpoint="/supervisors"
    title="Supervisores"
    testPrefix="sup"
    fields={[
      { key: "nome", label: "Nome", required: true },
      { key: "cpf", label: "CPF", required: true },
      { key: "matricula", label: "Matrícula", required: true },
      { key: "telefone", label: "Telefone" },
      { key: "email", label: "Email" },
      { key: "senha_temporaria", label: "Senha temporária (obrigatória no novo)", type: "password" },
    ]}
    columns={[
      { key: "nome", label: "Nome" },
      { key: "matricula", label: "Matrícula" },
      { key: "telefone", label: "Telefone" },
      { key: "email", label: "Email" },
    ]}
  />;
}

/* ---------------- ROTAS ---------------- */
function RotasTab() {
  return <SimpleCadastro
    endpoint="/routes"
    title="Rotas"
    testPrefix="route"
    fields={[
      { key: "nome", label: "Nome da rota", required: true },
      { key: "codigo", label: "Código" },
      { key: "cidade", label: "Cidade" },
      { key: "estado", label: "Estado (UF)" },
      { key: "supervisor", label: "Supervisor responsável" },
    ]}
    columns={[
      { key: "codigo", label: "Código" },
      { key: "nome", label: "Nome" },
      { key: "cidade", label: "Cidade" },
      { key: "estado", label: "UF" },
      { key: "supervisor", label: "Supervisor" },
    ]}
  />;
}

/* ---------------- CENTROS OPERACIONAIS ---------------- */
function COsTab() {
  return <SimpleCadastro
    endpoint="/cos"
    title="Centros Operacionais"
    testPrefix="co"
    fields={[
      { key: "nome", label: "Nome", required: true },
      { key: "sigla", label: "Sigla" },
      { key: "cidade", label: "Cidade" },
      { key: "estado", label: "Estado (UF)" },
    ]}
    columns={[
      { key: "sigla", label: "Sigla" },
      { key: "nome", label: "Nome" },
      { key: "cidade", label: "Cidade" },
      { key: "estado", label: "UF" },
    ]}
  />;
}

/* ---------------- USUÁRIOS ---------------- */
const USER_ROLES = [
  { v: "admin", label: "Administrador" },
  { v: "operacional", label: "Operacional" },
];
const emptyUser = { nome: "", email: "", telefone: "", login: "", senha: "trocar123", role: "operacional", status: "ativo" };

function UsuariosTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/users"); setRows(data || []); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => !q || r.nome.toLowerCase().includes(q.toLowerCase()) || (r.matricula || "").toLowerCase().includes(q.toLowerCase()));

  function openCreate() { setEditing(null); setForm(emptyUser); setOpen(true); }
  function openEdit(u) { setEditing(u); setForm({ ...emptyUser, ...u, login: u.matricula, senha: "" }); setOpen(true); }

  async function save() {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          nome: form.nome, email: form.email, telefone: form.telefone, role: form.role, status: form.status,
        });
        toast.success("Usuário atualizado");
      } else {
        await api.post("/users", form);
        toast.success("Usuário cadastrado");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }
  async function resetPwd(u) {
    try {
      const r = await api.post(`/users/${u.id}/reset-password`);
      toast.success(`Nova senha: ${r.data.temp_password}`);
    } catch (err) { toast.error(formatApiError(err)); }
  }
  async function del(u) {
    if (!window.confirm(`Excluir usuário ${u.nome}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Excluído"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Buscar por nome ou login" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="users-search-input" />
        </div>
        <Button onClick={openCreate} className="ml-auto bg-blue-900 hover:bg-blue-800" data-testid="users-add-btn">
          <Plus className="mr-1.5 h-4 w-4" /> Novo Usuário
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Login</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Nível</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="py-10 text-center text-slate-400">Carregando…</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-slate-400">Nenhum usuário.</TableCell></TableRow>}
            {filtered.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium text-slate-900">{u.nome}</TableCell>
                <TableCell>{u.matricula}</TableCell>
                <TableCell className="text-slate-600">{u.email || "—"}</TableCell>
                <TableCell className="text-slate-600">{u.telefone || "—"}</TableCell>
                <TableCell><Badge className="border bg-blue-50 text-blue-800 border-blue-200">{u.role}</Badge></TableCell>
                <TableCell><Badge className={`border ${u.status === "ativo" ? "bg-green-100 text-green-800 border-green-200" : "bg-slate-200"}`}>{u.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <IconBtn onClick={() => openEdit(u)} title="Editar" data-testid={`user-edit-${u.id}`}><Edit className="h-4 w-4" /></IconBtn>
                    <IconBtn onClick={() => resetPwd(u)} title="Resetar senha" data-testid={`user-reset-${u.id}`}><KeyRound className="h-4 w-4" /></IconBtn>
                    <IconBtn onClick={() => del(u)} title="Excluir" data-testid={`user-delete-${u.id}`}><Trash2 className="h-4 w-4 text-red-600" /></IconBtn>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Editar usuário" : "Novo usuário"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nome" full value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} testId="user-form-nome" />
            <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="user-form-email" />
            <Field label="Telefone" value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v })} testId="user-form-telefone" />
            {!editing && (
              <>
                <Field label="Login (username ou CPF)" value={form.login} onChange={(v) => setForm({ ...form, login: v })} testId="user-form-login" />
                <Field label="Senha temporária" value={form.senha} onChange={(v) => setForm({ ...form, senha: v })} testId="user-form-senha" />
              </>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Nível de acesso</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="user-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="user-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Generic SimpleCadastro ---------------- */
function SimpleCadastro({ endpoint, title, fields, columns, testPrefix }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get(endpoint); setRows(data || []); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const filtered = rows.filter((r) => !q || (r.nome || "").toLowerCase().includes(q.toLowerCase()));

  function openCreate() {
    const init = { status: "ativo" };
    fields.forEach((f) => { init[f.key] = ""; });
    setForm(init); setEditing(null); setOpen(true);
  }
  function openEdit(r) { setForm({ ...r }); setEditing(r); setOpen(true); }

  async function save() {
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((f) => { payload[f.key] = form[f.key] || ""; });
      payload.status = form.status || "ativo";
      for (const f of fields) if (f.required && !payload[f.key]) { toast.error(`${f.label} é obrigatório`); setSaving(false); return; }
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Atualizado" : "Cadastrado");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }
  async function del(r) {
    if (!window.confirm("Excluir?")) return;
    try { await api.delete(`${endpoint}/${r.id}`); toast.success("Excluído"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Buscar" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid={`${testPrefix}-search`} />
        </div>
        <Button onClick={openCreate} className="ml-auto bg-blue-900 hover:bg-blue-800" data-testid={`${testPrefix}-add-btn`}>
          <Plus className="mr-1.5 h-4 w-4" /> Novo
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={columns.length + 2} className="py-10 text-center text-slate-400">Carregando…</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={columns.length + 2} className="py-10 text-center text-slate-400">Nenhum registro.</TableCell></TableRow>}
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`${testPrefix}-row-${r.id}`}>
                {columns.map((c) => <TableCell key={c.key}>{r[c.key] || "—"}</TableCell>)}
                <TableCell>
                  <Badge className={`border ${r.status === "ativo" ? "bg-green-100 text-green-800 border-green-200" : "bg-slate-200"}`}>{r.status || "ativo"}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <IconBtn onClick={() => openEdit(r)} title="Editar" data-testid={`${testPrefix}-edit-${r.id}`}><Edit className="h-4 w-4" /></IconBtn>
                    <IconBtn onClick={() => del(r)} title="Excluir" data-testid={`${testPrefix}-delete-${r.id}`}><Trash2 className="h-4 w-4 text-red-600" /></IconBtn>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? `Editar ${title.slice(0, -1)}` : `Novo ${title.slice(0, -1).toLowerCase()}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {fields.map((f) => (
              <Field key={f.key} type={f.type || "text"} label={f.label + (f.required ? " *" : "")} value={form[f.key] || ""} onChange={(v) => setForm({ ...form, [f.key]: v })} testId={`${testPrefix}-form-${f.key}`} />
            ))}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Status</Label>
              <Select value={form.status || "ativo"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid={`${testPrefix}-form-status`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid={`${testPrefix}-form-save`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, full, testId, type = "text" }) {
  return (
    <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

function IconBtn({ children, ...rest }) {
  return (
    <button {...rest} className="rounded-md border border-slate-200 bg-white p-2 text-slate-600 transition hover:border-blue-700 hover:text-blue-700">
      {children}
    </button>
  );
}

function KV({ label, v }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-medium text-slate-900">{v || "—"}</p>
    </div>
  );
}
